import { createRoute, getAllRoutes, setRoutePermission, updateRoute } from './http/labRoutes';
import type { FetchQueryOptions, MutationOptions } from '@tanstack/query-core';
import type { CreateRouteRequest } from '@thinkalpha/locates-ws-client';
import type { RoutePermissionMutationPayload } from '@thinkalpha/ui-types/types/admin/lab';
import type { ReactBindings } from 'src/types/bindings';

// Models may not import `queries/http/**`; `UserRouteEntitlementsModel` writes through this.
export { setRoutePermission } from './http/labRoutes';

/**
 * Re-export of the `PATCH /v1/routes/:id` write so model code can call it without violating the
 * `no-restricted-imports` rule against `queries/http/**` paths.
 */
export { updateRoute };

/**
 * QueryKey Constants
 */
export const LAB_ROUTES = 'lab-routes';

/**
 * Query for retrieving routes the caller has access to. When `firmId` is provided it is sent as a
 * filter so callers with access to multiple firms only see the routes the viewed firm authored.
 * `firmId` is included in the query key so switching firms does not serve stale cached data. Toggling
 * superuser does not need a key dimension here: QueryCacheUserService resets all user-scoped queries
 * when the effective identity changes.
 */
export const getAllRoutesQuery = (firmId?: string): FetchQueryOptions<Awaited<ReturnType<typeof getAllRoutes>>> => {
    return {
        queryKey: firmId ? [LAB_ROUTES, 'list', 'firm', firmId] : [LAB_ROUTES, 'list'],
        queryFn: () => getAllRoutes(firmId),
    };
};

/**
 * Mutation for creating a route.
 */
export const createRouteMutation = (): MutationOptions<
    Awaited<ReturnType<typeof createRoute>>,
    Error,
    CreateRouteRequest
> => {
    return {
        mutationFn: createRoute,
    };
};

/**
 * Mutation for setting permissions of an existing route.
 */
export const setRoutePermissionMutation = (): MutationOptions<
    Awaited<ReturnType<typeof setRoutePermission>>,
    Error,
    RoutePermissionMutationPayload
> => {
    return {
        mutationFn: setRoutePermission,
    };
};

/**
 * Invalidation for all lab routes query, or individual route if specified
 */
export const invalidateRoutesQuery = (
    queryClient: ReactBindings['QueryClient'],
    routeId: string | undefined = undefined,
) => {
    return routeId
        ? queryClient.invalidateQueries({ queryKey: [LAB_ROUTES, routeId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_ROUTES] });
};
